import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Trophy, Users, Calendar, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatCompDate(yyyymmdd) {
    if (!yyyymmdd || yyyymmdd.length < 8) return yyyymmdd;
    const d = new Date(
        parseInt(yyyymmdd.slice(0, 4), 10),
        parseInt(yyyymmdd.slice(4, 6), 10) - 1,
        parseInt(yyyymmdd.slice(6, 8), 10)
    );
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function FootballJoinPage() {
    const { inviteCode } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();

    const [comp, setComp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [joining, setJoining] = useState(false);

    useEffect(() => {
        if (!inviteCode) return;
        axios.get(`${API_URL}/api/football/competitions/join/${inviteCode}`)
            .then(res => setComp(res.data))
            .catch(() => toast.error('Invalid invite link'))
            .finally(() => setLoading(false));
    }, [inviteCode]);

    async function handleJoin() {
        if (!isAuthenticated) {
            navigate(`/register?redirect=/football/join/${inviteCode}`);
            return;
        }
        setJoining(true);
        try {
            const res = await axios.post(
                `${API_URL}/api/football/competitions/join/${inviteCode}`,
                {},
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            toast.success(res.data.already_joined ? 'You\'ve already joined this competition' : 'Successfully joined!');
            navigate(`/football/${res.data.comp_id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to join competition');
        } finally {
            setJoining(false);
        }
    }

    if (loading) {
        return (
            <div className="container-app py-8 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!comp) {
        return (
            <div className="container-app py-8 text-center">
                <p className="text-muted-foreground">This invite link is not valid.</p>
                <Button className="mt-4" variant="outline" onClick={() => navigate('/football')}>
                    Browse competitions
                </Button>
            </div>
        );
    }

    return (
        <div className="container-app py-8">
            <div className="max-w-lg mx-auto">
                <Card className="border-primary/20 bg-gradient-to-b from-primary/5 to-transparent">
                    <CardHeader className="text-center pb-4">
                        <div className="mx-auto w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                            <Trophy className="h-8 w-8 text-primary" />
                        </div>
                        <CardTitle className="text-2xl font-heading">{comp.name}</CardTitle>
                        {comp.description && (
                            <CardDescription className="text-base">{comp.description}</CardDescription>
                        )}
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4 text-center">
                            <div className="p-4 rounded-lg bg-muted/50">
                                <Calendar className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                                <p className="text-sm text-muted-foreground">Start date</p>
                                <p className="font-semibold">{formatCompDate(comp.start_date)}</p>
                            </div>
                            <div className="p-4 rounded-lg bg-muted/50">
                                <Users className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                                <p className="text-sm text-muted-foreground">Entries</p>
                                <p className="font-semibold">{comp.entry_count || 0}</p>
                            </div>
                        </div>
                        {comp.end_date && (
                            <div className="p-4 rounded-lg bg-muted/50">
                                <p className="text-sm text-muted-foreground mb-1">Competition dates</p>
                                <p className="font-semibold">
                                    {formatCompDate(comp.start_date)} – {formatCompDate(comp.end_date)}
                                </p>
                            </div>
                        )}
                    </CardContent>
                    <CardFooter className="flex flex-col gap-3">
                        <Button
                            className="w-full btn-pill"
                            size="lg"
                            onClick={handleJoin}
                            disabled={joining}
                        >
                            {joining ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Joining...</> : isAuthenticated ? 'Join Competition' : 'Sign Up to Join'}
                        </Button>
                        {!isAuthenticated && (
                            <p className="text-sm text-muted-foreground text-center">
                                Already have an account?{' '}
                                <Link to={`/login?redirect=/football/join/${inviteCode}`} className="text-primary hover:underline">
                                    Sign in
                                </Link>
                            </p>
                        )}
                    </CardFooter>
                </Card>
            </div>
        </div>
    );
}
